// A sheet from the foot of a phone's screen: over a dimmed board, closed by its Close, a tap outside it, Esc, Back
// (PhoneChrome), or dragging it down by its top. A sheet given a place to keep opens where it was last scrolled to.

import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cx } from "../lib/util";

/** How far each kept sheet was scrolled, for this visit (Fixes 9: the Boards sheet no longer jumps back to its top). */
const places = new Map<string, number>();

export function Sheet({ label, title, onClose, children, className, keep }: { label: string; title?: ReactNode; onClose: () => void; children: ReactNode; className?: string; /** keeps its scroll place under this name */ keep?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const shut = useRef(onClose);
  shut.current = onClose;
  useLayoutEffect(() => {
    const el = body.current;
    if (!keep || !el) return;
    el.scrollTop = places.get(keep) ?? 0;
    const note = () => places.set(keep, el.scrollTop);
    el.addEventListener("scroll", note, { passive: true });
    return () => el.removeEventListener("scroll", note);
  }, [keep]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && shut.current();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  // dragged down by its grip or its head: it follows the finger, and closes past a third of its height
  useEffect(() => {
    const el = ref.current;
    const grab = el?.querySelector<HTMLElement>(".psheet-top");
    if (!el || !grab) return;
    let y0 = 0;
    let dy = 0;
    let on = false;
    const down = (e: TouchEvent) => {
      on = e.touches.length === 1;
      y0 = e.touches[0].clientY;
      dy = 0;
    };
    const move = (e: TouchEvent) => {
      if (!on) return;
      dy = Math.max(0, e.touches[0].clientY - y0);
      el.style.transition = "none";
      el.style.transform = `translateY(${dy}px)`;
      if (e.cancelable) e.preventDefault();
    };
    const up = () => {
      if (!on) return;
      on = false;
      el.style.transition = "";
      if (dy > Math.min(160, el.offsetHeight / 3)) shut.current();
      else el.style.transform = "";
    };
    grab.addEventListener("touchstart", down, { passive: true });
    grab.addEventListener("touchmove", move, { passive: false });
    grab.addEventListener("touchend", up);
    grab.addEventListener("touchcancel", up);
    return () => {
      grab.removeEventListener("touchstart", down);
      grab.removeEventListener("touchmove", move);
      grab.removeEventListener("touchend", up);
      grab.removeEventListener("touchcancel", up);
    };
  }, []);
  return (
    <div className="psheet-scrim" onClick={onClose}>
      <div ref={ref} className={cx("psheet", className)} role="dialog" aria-modal="true" aria-label={label} onClick={(e) => e.stopPropagation()}>
        <div className="psheet-top">
          <span className="psheet-grip" aria-hidden />
          <div className="psheet-head">
            {title}
            <button className="icon-btn psheet-x" aria-label={`Close ${label}`} data-tip="Close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>
        <div ref={body} className="psheet-body">
          {children}
        </div>
      </div>
    </div>
  );
}

/** One row in a sheet: an icon, its words, and what it does. */
export function SheetRow({ icon, label, hint, onClick, danger, disabled, on, className }: { icon?: ReactNode; label: ReactNode; hint?: ReactNode; onClick: () => void; danger?: boolean; disabled?: boolean; on?: boolean; className?: string }) {
  return (
    <button className={cx("psheet-row", danger && "is-danger", on && "is-on", className)} onClick={onClick} disabled={disabled} aria-current={on ? "true" : undefined}>
      {icon && <span className="psheet-ico">{icon}</span>}
      <span className="psheet-label">{label}</span>
      {hint && <span className="psheet-hint">{hint}</span>}
    </button>
  );
}
